import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, sortBy } from 'lodash';
import classNames from 'classnames';

import { Fields } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';

import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';

import IconArrowLeft from 'components/icons/IconArrowLeft';

import {
  requestFetchWorkflowStatuses,
  requestSearchWorkflowStatuses,
} from 'redux/modules/workflowStatuses';

import * as validate from 'utils/validate';

import { accountLoginFail } from 'redux/modules/account';
import { saveRecoverOrder } from 'redux/modules/recoverOrder';

const styles = theme => ({
  root: {
    marginTop: 10,
  },
  iconArrowLeft: {
    width: 10,
    height: 10,
    margin: '-2px 0 0 10px',
    verticalAlign: 'middle',
    transform: 'rotate(180deg)',
    color: 'inherit',
  },
  suggestionAutoChange: {
    color: 'inherit',
  },
  state: {
    display: 'inline-block',
    margin: '-2px 0 0 10px',
    width: 12,
    height: 12,
    fontSize: 10,
    lineHeight: '14px',
    padding: 0,
    textAlign: 'center',
    verticalAlign: 'middle',
    color: theme.palette.common.white,
    borderRadius: '50%',
  },
  stateIntake: {
    backgroundColor: theme.palette.states.intake,
  },
  stateDelivery: {
    backgroundColor: theme.palette.states.delivery,
  },
  stateBilling: {
    backgroundColor: theme.palette.states.billing,
  },
  stateCollection: {
    backgroundColor: theme.palette.states.collection,
  },
  suggestionContainerIntake: {
    color: theme.palette.states.intake,
  },
  suggestionContainerDelivery: {
    color: theme.palette.states.delivery,
  },
  suggestionContainerBilling: {
    color: theme.palette.states.billing,
  },
  suggestionContainerCollection: {
    color: theme.palette.states.collection,
  },
});

const ProductClaimViewItemStatusFields = ({
  name,
  onFieldFocus,
  onFieldBlur,
  actions,
  account,
  disabled,
  classes,
  ...fields
}) => {
  const statusField = get(fields, `${name}.Status`);
  const selectedStatusIDs = statusField.input.value ? statusField.input.value.map(item => item.value) : [];
  return (
    <Fields
      id={`${name}.Status`}
      names={[
        `${name}.Status Name`,
        `${name}.Status`,
      ]}
      autosuggestFieldName={`${name}.Status Name`}
      fieldArrayName={`${name}.Status`}
      component={MultiselectAutosuggestField}
      validate={[validate.required]}
      placeholder="-Status-"
      fullWidth
      listWidth="auto"
      disabled={disabled}
      renderInputContent={suggestion => suggestion.Name}
      renderSuggestionContent={suggestion => (
        (suggestion['Next Workflow Status'] && suggestion['Next Workflow Status'].ID) ? (
          <div
            className={classNames({
              [classes.suggestionContainerIntake]: suggestion['Next Workflow Status']['Workflow State'].ID === 1,
              [classes.suggestionContainerDelivery]: suggestion['Next Workflow Status']['Workflow State'].ID === 2,
              [classes.suggestionContainerBilling]: suggestion['Next Workflow Status']['Workflow State'].ID === 3,
              [classes.suggestionContainerCollection]: suggestion['Next Workflow Status']['Workflow State'].ID === 4,
            })}
          >
            <span className={classes.suggestionAutoChange}>
              {suggestion.Name}
            </span>
            <IconArrowLeft className={classes.iconArrowLeft} />
            {suggestion['Next Workflow Status']['Workflow State'].ID === 1 && (
              <span className={classNames(classes.state, classes.stateIntake)}>
                I
              </span>
            )}
            {suggestion['Next Workflow Status']['Workflow State'].ID === 2 && (
              <span className={classNames(classes.state, classes.stateDelivery)}>
                D
              </span>
            )}
            {suggestion['Next Workflow Status']['Workflow State'].ID === 3 && (
              <span className={classNames(classes.state, classes.stateBilling)}>
                B
              </span>
            )}
            {suggestion['Next Workflow Status']['Workflow State'].ID === 4 && (
              <span className={classNames(classes.state, classes.stateCollection)}>
                C
              </span>
            )}
          </div>
        ) : (
          <div>
            {suggestion.Name}
          </div>
        )
      )}
      onSuggestionSelected={(suggestion) => {
        let newStatusValue = [
          ...statusField.input.value || [],
          {
            value: suggestion.ID,
            label: suggestion.Name,
            ...suggestion['Is Delivered'] ? {
              isDelivered: true,
            } : {},
            ...suggestion['Next Workflow Status'] && suggestion['Next Workflow Status'].ID ? {
              nextWorkflowStatusID: suggestion['Next Workflow Status'].ID,
            } : {},
          },
        ];
        statusField.input.onChange(sortBy(newStatusValue, 'value'));
        return '';
      }}
      fetchSuggestions={({ value, limit, offset }) => (
        value.length > 0 || selectedStatusIDs.length > 0
          ? requestSearchWorkflowStatuses({
            filters: [
              ...value.length > 0 ? [{
                name: 'Name',
                operation: 'contains',
                val: value,
                case_sensitivity: false,
              }] : [],
              ...selectedStatusIDs.map(item => ({
                name: 'ID',
                operation: 'ne',
                val: item,
              })),
            ],
            limit,
            offset,
          }).then(res => res.data,
            (err) => {
              if (err.data.error === 'Access Error') {
                actions.accountLoginFail();
              }
              return err;
            })
          : requestFetchWorkflowStatuses({
            limit,
            offset,
          }).then(res => res.data,
            (err) => {
              if (err.data.error === 'Access Error') {
                actions.accountLoginFail();
              }
              return err;
            })
      )}
    />
  );
};

ProductClaimViewItemStatusFields.propTypes = {
  name: PropTypes.string.isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
    saveRecoverOrder: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    iconArrowLeft: PropTypes.string.isRequired,
    suggestionAutoChange: PropTypes.string.isRequired,
  }).isRequired,
};

ProductClaimViewItemStatusFields.defaultProps = {
  disabled: false,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail, saveRecoverOrder }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(ProductClaimViewItemStatusFields),
);



// WEBPACK FOOTER //
// ./src/components/workflow/ProductClaimView/ProductClaimViewItemStatusFields.js